import {
  FC,
  MouseEventHandler,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { observer } from 'mobx-react-lite';
import { Button, Dropdown, HStack, Text } from 'components/ui';
import { SVG } from 'components/svg';
import { Icons } from 'assets';

type PaginatorProps = {
  count: number;
  limitPerPage: number;
  offset: number;
  action: (limit: number, offset: number) => Promise<any>;
  limitPerPageVariants?: number[];
  maxPages?: number;
  showCurrentPageRange?: boolean;
};

type Page = { value: number; text: string };

/** Count - общее количество элементов в пагинации. Если оно заранее неизвестно, воспользоваться хуком useDynamicPaginatorCount.
 * Лежит по соседству в этой же папке, добавлен в публичный API через index.ts.
 * limitPerPage - количество элементов на пагинированной странице.
 * offset - текущий offset.
 * action - Promise. Запрос необходимой страницы. Если это возврат на уже закэшированную страницу, необходимо просто обернусь
 * результат в моментально резольвящийся промис: new Promise((res) => { if (cache.element) { res(cache.element) } else { request... } })).
 * maxPages - необязательный аргумент, максимальное количество отображаемых страниц. Текущая/первая/последняя входят в это значение.
 * По умолчание количество страниц равно 5. */
export const Paginator: FC<PaginatorProps> = observer(
  ({
    count,
    limitPerPage,
    offset,
    action,
    limitPerPageVariants,
    maxPages = 5,
    showCurrentPageRange = true,
  }) => {
    const [selectedPage, setSelectedPage] = useState<number>(
      Math.floor(offset / limitPerPage) + 1
    );
    const [isPending, setIsPending] = useState<boolean>(false);
    const isMounted = useRef<boolean>(false);
    const [isLimitVariantsOpen, setLimitVariantsOpen] =
      useState<boolean>(false);

    useEffect(() => {
      isMounted.current = true;

      return () => {
        isMounted.current = false;
      };
    }, []);

    useEffect(() => {
      setSelectedPage(Math.floor(offset / limitPerPage) + 1);
    }, [offset, limitPerPage]);

    const pagesCount = useMemo<number>(
      () => Math.ceil(count / limitPerPage),
      [count, limitPerPage]
    );
    const skipPage = useMemo<Page>(() => ({ value: 0, text: '...' }), []);
    const allPages = useMemo<Page[]>(() => {
      return Array.from({ length: pagesCount }).map((_, i) => ({
        value: i + 1,
        text: (i + 1).toString(),
      }));
    }, [pagesCount]);

    const currentPageRangeText = useMemo(
      () =>
        `${offset + 1}-${Math.min(offset + limitPerPage, count)} из ${count}`,
      [offset, limitPerPage, count]
    );

    const visiblePages: Page[] = useMemo(() => {
      if (maxPages - selectedPage > 1) {
        return [
          allPages.slice(0, maxPages - 1),
          skipPage,
          allPages.slice(-1),
        ].flat();
      }

      /* Количество видимых страниц по сторонам от выбранной.
       * -3, т.к. мы всегда видим первую, последнюю и выбранную страницу, их учитывать не нужно */
      const sideStep = (maxPages - 3) / 2;

      return [
        allPages[0],
        skipPage,
        allPages.slice(
          selectedPage - Math.floor(sideStep) - 1, // -1, т.к. slice отдает массив после указанного элемента
          selectedPage + Math.ceil(sideStep)
        ),
        selectedPage < pagesCount - 1
          ? [skipPage, allPages.slice(-1)].flat()
          : [],
      ].flat();
    }, [allPages, maxPages, pagesCount, selectedPage, skipPage]);

    const pageHandler: MouseEventHandler<HTMLButtonElement> = useCallback(
      async (e) => {
        e.preventDefault();
        const currentPage: number = +e.currentTarget.value;

        if (currentPage !== selectedPage) {
          setSelectedPage(currentPage);
          setIsPending(true);

          try {
            await action(limitPerPage, (currentPage - 1) * limitPerPage);
          } finally {
            isMounted.current && setIsPending(false);
          }
        }
      },
      [action, limitPerPage, selectedPage]
    );

    const limitHandler = useCallback(
      async (newLimit: number) => {
        if (isPending || newLimit === limitPerPage) {
          return;
        }

        setSelectedPage(1);
        setIsPending(true);

        try {
          await action(newLimit, 0);
        } finally {
          isMounted.current && setIsPending(false);
        }
      },
      [action, isPending, limitPerPage]
    );

    const pages = pagesCount > maxPages ? visiblePages : allPages;

    return (
      <HStack align="center" justify="space-between" width="100%">
        {showCurrentPageRange && (
          <Text variant="body-14" color="gray.500">
            {currentPageRangeText}
          </Text>
        )}
        <HStack align="center" spacing="base">
          <HStack spacing={'xs'}>
            {pages.map((page, index) => (
              <Button
                key={index}
                mode="neutral"
                selected={page.value === selectedPage}
                size="s"
                disabled={
                  (isPending || !page.value) && page.value !== selectedPage
                }
                pending={page.value === selectedPage && isPending}
                value={page.value}
                onClick={pageHandler}
              >
                {page.text}
              </Button>
            ))}
          </HStack>
          {!!limitPerPageVariants?.length && (
            <Dropdown.Root
              open={isLimitVariantsOpen}
              onOpenChange={setLimitVariantsOpen}
              modal={false}
            >
              <Dropdown.Trigger asChild>
                <Button
                  rightIcon={
                    <SVG
                      src={Icons.Chevron}
                      props={{
                        isMirror: isLimitVariantsOpen,
                      }}
                    />
                  }
                  variant="minor"
                  mode="neutral"
                  size="s"
                  disabled={isPending}
                >
                  {limitPerPage} на странице
                </Button>
              </Dropdown.Trigger>
              <Dropdown.Portal>
                <Dropdown.Content align="end">
                  {limitPerPageVariants.map((limitValue) => (
                    <Dropdown.CheckboxItem
                      key={limitValue}
                      checked={limitPerPage === limitValue}
                      onSelect={() => {
                        void limitHandler(limitValue);
                      }}
                    >
                      <Text>{limitValue} на странице</Text>

                      <Dropdown.ItemIndicator>
                        <SVG src={Icons.Check} />
                      </Dropdown.ItemIndicator>
                    </Dropdown.CheckboxItem>
                  ))}
                </Dropdown.Content>
              </Dropdown.Portal>
            </Dropdown.Root>
          )}
        </HStack>
      </HStack>
    );
  }
);
